import React from 'react';
import { Handle, Position } from 'reactflow';

// Enhanced Base Node Component
const BaseNode = ({ data, children, color = '#3b82f6', icon, nodeType }) => {
    const getBorderStyle = () => ({
        borderColor: color,
        borderWidth: '2px',
        borderStyle: 'solid',
        boxShadow: `0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 3px ${color}15`
    });

    const getHeaderStyle = () => ({
        background: `linear-gradient(135deg, ${color}15 0%, ${color}25 100%)`,
        borderBottom: `1px solid ${color}30`
    });

    return (
        <div
            className="bg-white rounded-lg min-w-[180px] max-w-[250px] overflow-hidden transition-all duration-200 hover:shadow-lg"
            style={getBorderStyle()}
        >
            {/* Node Header */}
            <div className="px-3 py-2" style={getHeaderStyle()}>
                <div className="flex items-center gap-2">
                    {icon && (
                        <span className="text-lg flex-shrink-0" role="img" aria-label={nodeType}>
                            {icon}
                        </span>
                    )}
                    <div className="font-semibold text-gray-800 text-sm truncate flex-1">
                        {data.label}
                    </div>
                </div>
            </div>

            {/* Node Content */}
            {children && (
                <div className="px-3 py-2">
                    {children}
                </div>
            )}
        </div>
    );
};

// Enhanced Start Node
export const StartNode = ({ data }) => (
    <div className="relative group">
        <BaseNode data={data} color="#10b981" icon="▶️" nodeType="start">
            <div className="text-xs text-gray-600 font-medium">
                Flow entry point
            </div>
        </BaseNode>
        <Handle
            type="source"
            position={Position.Bottom}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#10b981',
                borderRadius: '50%'
            }}
        />
    </div>
);

// Enhanced Message Node
export const MessageNode = ({ data }) => (
    <div className="relative group">
        <Handle
            type="target"
            position={Position.Top}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#3b82f6',
                borderRadius: '50%'
            }}
        />
        <BaseNode data={data} color="#3b82f6" icon="💬" nodeType="message">
            {data.message && (
                <div className="mb-2">
                    <div className="text-xs text-gray-500 font-medium mb-1">Message:</div>
                    <div className="text-xs text-gray-700 line-clamp-2 bg-gray-50 p-2 rounded">
                        {data.message}
                    </div>
                </div>
            )}
            {data.media && data.media.enabled && (
                <div className="mb-2">
                    <div className="flex items-center gap-1 mb-1">
                        <div className="text-xs text-gray-500 font-medium">Media:</div>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full capitalize">
                            {data.media.type || 'file'}
                        </span>
                    </div>
                    <div className="text-xs bg-gray-50 p-2 rounded flex items-center gap-2">
                        {data.media.type === 'image' && <span role="img" aria-label="image">🖼️</span>}
                        {data.media.type === 'video' && <span role="img" aria-label="video">🎬</span>}
                        {data.media.type === 'audio' && <span role="img" aria-label="audio">🔊</span>}
                        {data.media.type === 'document' && <span role="img" aria-label="document">📄</span>}
                        <span className="line-clamp-1 flex-1 text-gray-700 font-mono text-[10px] truncate">
                            {data.media.url}
                        </span>
                    </div>
                </div>
            )}
        </BaseNode>
        <Handle
            type="source"
            position={Position.Bottom}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#3b82f6',
                borderRadius: '50%'
            }}
        />
    </div>
);

// Enhanced Question Node
export const QuestionNode = ({ data }) => (
    <div className="relative group">
        <Handle
            type="target"
            position={Position.Top}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#8b5cf6',
                borderRadius: '50%'
            }}
        />
        <BaseNode data={data} color="#8b5cf6" icon="❓" nodeType="question">
            {data.message && (
                <div className="mb-2">
                    <div className="text-xs text-gray-500 font-medium mb-1">Question:</div>
                    <div className="text-xs text-gray-700 line-clamp-2 bg-gray-50 p-2 rounded">
                        {data.message}
                    </div>
                </div>
            )}
            {data.variable && (
                <div className="mb-2">
                    <div className="text-xs text-gray-500 font-medium mb-1">Stores in:</div>
                    <div className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-mono">
                        {data.variable}
                    </div>
                </div>
            )}
            {data.quickReplies && data.quickReplies.length > 0 && (
                <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                    <span className="text-xs text-purple-600 font-medium">
                        {data.quickReplies.length} quick repl{data.quickReplies.length !== 1 ? 'ies' : 'y'}
                    </span>
                </div>
            )}
        </BaseNode>
        <Handle
            type="source"
            position={Position.Bottom}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#8b5cf6',
                borderRadius: '50%'
            }}
        />
    </div>
);

// Enhanced Condition Node
export const ConditionNode = ({ data }) => (
    <div className="relative group">
        <Handle
            type="target"
            position={Position.Top}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#f59e0b',
                borderRadius: '50%'
            }}
        />
        <BaseNode data={data} color="#f59e0b" icon="🔀" nodeType="condition">
            {data.condition && (
                <div className="space-y-2">
                    <div className="text-xs text-gray-500 font-medium">Condition:</div>
                    <div className="bg-orange-50 p-2 rounded text-xs">
                        <div className="font-mono text-orange-800">
                            <span className="text-blue-600">{data.condition.field || 'field'}</span>
                            <span className="text-gray-600 mx-1">{data.condition.operator || 'equals'}</span>
                            <span className="text-green-600">"{data.condition.value || 'value'}"</span>
                        </div>
                    </div>
                </div>
            )}
        </BaseNode>

        {/* True Handle */}
        <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#10b981',
                left: '30%',
                borderRadius: '50%'
            }}
        />

        {/* False Handle */}
        <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#ef4444',
                left: '70%',
                borderRadius: '50%'
            }}
        />

        {/* Handle Labels */}
        <div className="absolute bottom-[-28px] left-[25%] transform -translate-x-1/2">
            <span className="text-xs text-green-600 font-semibold bg-white px-1 rounded shadow-sm border">
                True
            </span>
        </div>
        <div className="absolute bottom-[-28px] left-[75%] transform -translate-x-1/2">
            <span className="text-xs text-red-600 font-semibold bg-white px-1 rounded shadow-sm border">
                False
            </span>
        </div>
    </div>
);

// Enhanced Action Node
export const ActionNode = ({ data }) => (
    <div className="relative group">
        <Handle
            type="target"
            position={Position.Top}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#ef4444',
                borderRadius: '50%'
            }}
        />
        <BaseNode data={data} color="#ef4444" icon="⚡" nodeType="action">
            {data.action && (
                <div className="space-y-2">
                    <div className="text-xs text-gray-500 font-medium">Action Type:</div>
                    <div className="bg-red-50 p-2 rounded">
                        <div className="text-xs font-semibold text-red-700 capitalize">
                            {data.action.type || 'setVariable'}
                        </div>
                        {data.action.config && (
                            <div className="text-xs text-red-600 mt-1 font-mono truncate">
                                {data.action.config.variable && (
                                    <span>{data.action.config.variable} = {data.action.config.value || '...'}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </BaseNode>
        <Handle
            type="source"
            position={Position.Bottom}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#ef4444',
                borderRadius: '50%'
            }}
        />
    </div>
);

// Enhanced Handoff Node
export const HandoffNode = ({ data }) => (
    <div className="relative group">
        <Handle
            type="target"
            position={Position.Top}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#06b6d4',
                borderRadius: '50%'
            }}
        />
        <BaseNode data={data} color="#06b6d4" icon="👤" nodeType="handoff">
            <div className="space-y-2">
                {data.handoffReason && (
                    <div>
                        <div className="text-xs text-gray-500 font-medium mb-1">Reason:</div>
                        <div className="text-xs text-gray-700 bg-cyan-50 p-2 rounded line-clamp-2">
                            {data.handoffReason}
                        </div>
                    </div>
                )}
                {data.message && (
                    <div>
                        <div className="text-xs text-gray-500 font-medium mb-1">Message:</div>
                        <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded line-clamp-2">
                            {data.message}
                        </div>
                    </div>
                )}
                <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                    <span className="text-xs text-cyan-600 font-medium">
                        Transfer to human
                    </span>
                </div>
            </div>
        </BaseNode>
        <Handle
            type="source"
            position={Position.Bottom}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#06b6d4',
                borderRadius: '50%'
            }}
        />
    </div>
);

// Enhanced End Node
export const EndNode = ({ data }) => (
    <div className="relative group">
        <Handle
            type="target"
            position={Position.Top}
            className="w-3 h-3 border-2 border-white shadow-md transition-all duration-200 group-hover:scale-110"
            style={{
                background: '#6b7280',
                borderRadius: '50%'
            }}
        />
        <BaseNode data={data} color="#6b7280" icon="🏁" nodeType="end">
            <div className="text-xs text-gray-600 font-medium">
                Flow termination
            </div>
        </BaseNode>
    </div>
);

// Export all nodes as default
export default {
    StartNode,
    MessageNode,
    QuestionNode,
    ConditionNode,
    ActionNode,
    HandoffNode,
    EndNode,
};